// Merge a user-applied sort with the chart's full column list so that any
// pagination across that sort stays deterministic.
//
// Background (SSP-3889): cube `LIMIT`/`OFFSET` across an `ORDER BY` with tied
// values returns inconsistent slices — the same row can appear on multiple
// pages while others are dropped. The chart-builder default order from
// buildQueryFromState includes every dimension + measure as a tiebreaker; the
// table sort handler used to replace that with a single-column order, which
// reintroduced the tie problem the moment the user clicked a header. The fix
// keeps the user's sort as primary and re-appends the remaining columns as
// ascending tiebreakers.
//
// Returned as the cube array form (`[[key, direction], ...]`) so key order is
// preserved end-to-end — the object form's iteration order is not part of the
// cube contract.
export const withSortTiebreakers = (primary, tiebreakerKeys) => {
    const primaryEntries = Object.entries(primary);
    const primaryKeys = new Set(primaryEntries.map(([key]) => key));
    const tiebreakers = tiebreakerKeys.filter((key) => !primaryKeys.has(key)).map((key) => [key, 'asc']);
    return [...primaryEntries, ...tiebreakers];
};
// Normalize a persisted builder default sort (SSP-3919) into a list of valid
// rules. Charts saved by the original single-column version store a lone
// `{ key, direction }` object instead of an array.
export const normalizeDefaultSort = (defaultSort) => {
    if (!defaultSort) {
        return undefined;
    }
    const rules = (Array.isArray(defaultSort) ? defaultSort : [defaultSort]).filter((rule) => rule?.key);
    return rules.length > 0 ? rules : undefined;
};
// Cube order for a chart's configured default sort: the rules first (in
// order), then the remaining columns as ascending tiebreakers — the same
// shape buildQueryFromState produces in the Library, so the study view
// renders the same row order (SSP-4500).
export const buildDefaultSortOrder = (rules, tiebreakerKeys) => withSortTiebreakers(rules.reduce((acc, rule) => {
    acc[rule.key] = rule.direction;
    return acc;
}, {}), tiebreakerKeys);
